import { useAnalytics } from '@satellite/composables/useAnalytics';
import type { TrackOptions } from '@satellite/analytics/AnalyticsClient';
import {
  Events,
  type SideNavClickedPayload,
  type WarehouseSelectorClickedPayload,
  type HeliosEventsMap
} from '@/analytics/events';

export type TrackSideNavClickedOptions = TrackOptions<SideNavClickedPayload>;
export type TrackWarehouseSelectorClickedOptions = TrackOptions<WarehouseSelectorClickedPayload>;

function getScreenSize(): string {
  return `${window.innerWidth}x${window.innerHeight}`;
}

export function useNavigationAnalytics() {
  const analytics = useAnalytics<HeliosEventsMap>();

  function trackAddAppointmentClicked(options: TrackSideNavClickedOptions = {}) {
    analytics.track(Events.NAVIGATION.ADD_APPOINTMENT_CLICKED, {
      screen_size: getScreenSize(),
      ...(options.additionalPayload ?? {})
    });
  }

  function trackSearchAppointmentsClicked(options: TrackSideNavClickedOptions = {}) {
    analytics.track(Events.NAVIGATION.SEARCH_APPOINTMENTS_CLICKED, {
      screen_size: getScreenSize(),
      ...(options.additionalPayload ?? {})
    });
  }

  function trackWarehousesClicked(options: TrackSideNavClickedOptions = {}) {
    analytics.track(Events.NAVIGATION.WAREHOUSES_CLICKED, {
      screen_size: getScreenSize(),
      ...(options.additionalPayload ?? {})
    });
  }

  function trackMyOrganizationClicked(options: TrackSideNavClickedOptions = {}) {
    analytics.track(Events.NAVIGATION.MY_ORGANIZATION_CLICKED, {
      screen_size: getScreenSize(),
      ...(options.additionalPayload ?? {})
    });
  }

  function trackUsersClicked(options: TrackSideNavClickedOptions = {}) {
    analytics.track(Events.NAVIGATION.USERS_CLICKED, {
      screen_size: getScreenSize(),
      ...(options.additionalPayload ?? {})
    });
  }

  function trackMyCarrierContactsClicked(options: TrackSideNavClickedOptions = {}) {
    analytics.track(Events.NAVIGATION.MY_CARRIER_CONTACTS_CLICKED, {
      screen_size: getScreenSize(),
      ...(options.additionalPayload ?? {})
    });
  }

  function trackInsightsClicked(options: TrackSideNavClickedOptions = {}) {
    analytics.track(Events.NAVIGATION.INSIGHTS_CLICKED, {
      screen_size: getScreenSize(),
      ...(options.additionalPayload ?? {})
    });
  }

  function trackWarehouseSelectorClicked(
    params: Omit<WarehouseSelectorClickedPayload, 'screen_size'>,
    options: TrackWarehouseSelectorClickedOptions = {}
  ) {
    analytics.track(Events.NAVIGATION.WAREHOUSE_SELECTOR_CLICKED, {
      screen_size: getScreenSize(),
      ...params,
      ...(options.additionalPayload ?? {})
    });
  }

  return {
    trackAddAppointmentClicked,
    trackSearchAppointmentsClicked,
    trackWarehousesClicked,
    trackMyOrganizationClicked,
    trackUsersClicked,
    trackMyCarrierContactsClicked,
    trackInsightsClicked,
    trackWarehouseSelectorClicked
  };
}
